import { htmlElements } from '../helpers/htmlElements'
import type { PaymentOption } from '../models/sdkModels'
import { togglePriceDisplayBetweenCreditCardAndACH } from './injectHtml'
import type { CheckoutHandler } from './sdk'

const tabList: string[] = []

const resetTabs = (): void => {
    const { tabContents, tabLinks } = htmlElements()
    Array.from(tabContents).forEach((tabContent) => {
        tabContent.style.display = 'none'
        tabContent.classList.remove('active')
    })

    Array.from(tabLinks).forEach((tabLink) => {
        tabLink.classList.remove('active')
        tabLink.setAttribute('tabindex', '-1')
        tabLink.setAttribute('aria-selected', 'false')
        tabLink.blur()
    })
}

const openTab = (
    tabName: 'ach' | 'card',
    checkoutInstance: CheckoutHandler,
    focus?: boolean,
): void => {
    const { tab, tabContent } = htmlElements()

    resetTabs()

    const selectedTab: HTMLElement = tabContent(tabName)
    selectedTab.style.display = 'block'
    selectedTab.classList.add('active')

    const targetTabLink: HTMLElement = tab(tabName)
    targetTabLink.classList.add('active')
    targetTabLink.setAttribute('tabindex', '0')
    targetTabLink.setAttribute('aria-selected', 'true')

    // Keyboard only - focus tab seleceted by the key event
    if (focus) {
        targetTabLink.focus()
    }
    togglePriceDisplayBetweenCreditCardAndACH(tabName, checkoutInstance)
}

const handleKeyEvent = (
    event: KeyboardEvent,
    tabName: string,
    checkoutInstance: CheckoutHandler,
): void => {
    const isLeftArrow = event.key === 'ArrowLeft'
    const isRightArrow = event.key === 'ArrowRight'
    const isHome = event.key === 'Home'
    const isEnd = event.key === 'End'

    if (isLeftArrow || isRightArrow || isHome || isEnd) {
        const currentTabIndex = tabList.indexOf(tabName)
        const lastTabIndex = tabList.length - 1
        const currentTabIsLastTab: boolean = currentTabIndex === lastTabIndex
        const currentTabIsFirstTab: boolean = currentTabIndex === 0
        let targetIndex = 0

        if (isRightArrow) {
            targetIndex = currentTabIsLastTab ? 0 : currentTabIndex + 1
        }

        if (isLeftArrow) {
            targetIndex = currentTabIsFirstTab
                ? lastTabIndex
                : currentTabIndex - 1
        }

        if (isHome) {
            targetIndex = 0
        }

        if (isEnd) {
            targetIndex = lastTabIndex
        }

        const target = tabList[targetIndex]
        checkoutInstance.setPaymentOption(target as PaymentOption)
        openTab(target as 'ach' | 'card', checkoutInstance, true)

        event.stopPropagation()
        event.preventDefault()
        // Prevents unwanted behavior - some browsers will jump to the start / end of the
        // document if the Home /End keys are used for switching tabs.
    }
}

export function initializeTabs(checkoutInstance: CheckoutHandler): void {
    const tabLinks = document.querySelectorAll('.gc-dg-tab-link')

    tabLinks.forEach((tabLink) => {
        const tabName = tabLink.getAttribute('data-tab')
        tabList.push(tabName as string)

        tabLink.addEventListener('click', (): void => {
            checkoutInstance.setPaymentOption(tabName as PaymentOption)
            openTab(tabName as 'ach' | 'card', checkoutInstance)
        })

        tabLink.addEventListener('keydown', (event: Event): void => {
            handleKeyEvent(
                event as KeyboardEvent,
                tabName as string,
                checkoutInstance,
            )
        })
    })

    // Open first tab
    if (tabList.length) {
        openTab(tabList[0] as 'ach' | 'card', checkoutInstance)
    }
}
